import React, { useEffect, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useFetchNotificationJobsMutation } from "../../../features/tenant/PushNotificationType/pushNotificationTypeAPi";
import CustomExport from "../../../components/CustomExport/CustomExport";

const NotificationHistory = () => {
  const navigate = useNavigate();
  const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme")).theme.color_shades;

  const [myList, setMyList] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);

  const initialRequest = {
    mobile: "",
    user_id: "",
    status: "",
    start_date: "",
    end_date: "",
    limit: 10,
    offset: 0,
  };

  const [request, setRequest] = useState(initialRequest);
  const [csvData, setCsvData] = useState(null);

  const [
    fetchNotificationJobs,
    {
      data: jobsResponse,
      isLoading: jobsLoading,
      error: jobsError,
    },
  ] = useFetchNotificationJobsMutation();

  const [
    fetchJobsForCsv,
    {
      data: csvResponse,
      isLoading: csvLoading,
      error: csvError,
    },
  ] = useFetchNotificationJobsMutation();

  const getAll = (currentRequest = request) => {
    fetchNotificationJobs({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      body: currentRequest,
    });
  };

  const getAllForCsv = () => {
    const csvRequest = {
      ...request,
      limit: null,
      offset: null,
    };
    fetchJobsForCsv({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      body: csvRequest,
    });
  };

  useEffect(() => {
    getAll();
  }, []);

  useEffect(() => {
    if (jobsResponse) {
      if (jobsResponse.success) {
        setTotalRows(jobsResponse.body?.count ? Number(jobsResponse.body?.count) : 0);
        setMyList(jobsResponse.body?.jobs ? jobsResponse.body?.jobs : []);
      } else {
        setTotalRows(0);
        setMyList([]);
      }
    }
    if (jobsError) {
      console.error("Error fetching notification jobs:", jobsError);
      toast.error(jobsError?.data?.message || "Failed to load notification history.");
    }
  }, [jobsResponse, jobsError]);

  useEffect(() => {
    if (csvLoading) {
      toast.dismiss();
      toast.loading("Preparing CSV download data...");
    }
    if (csvResponse) {
      toast.dismiss();
      if (csvResponse.success && csvResponse.body?.jobs) {
        const flattened = csvResponse.body.jobs.map((item, idx) => ({
          s_no: idx + 1,
          name: item.name || "-",
          mobile: item.mobile || "-",
          email: item.email || "-",
          user_id: item.user_id || "-",
          notification_type: item.notification?.notification_type || "-",
          notification_title: item.notification?.title || "-",
          notification_body: item.notification?.body || "-",
          status: item.status || "-",
          sent_at: item.sent_at ? new Date(item.sent_at).toLocaleString() : "-",
          last_error: item.last_error || "-",
        }));
        setCsvData(flattened);
      } else {
        toast.error("No data available to export.");
        setCsvData([]);
      }
    }
    if (csvError) {
      toast.dismiss();
      console.error("Error fetching CSV jobs:", csvError);
      toast.error("Failed to prepare CSV download.");
      setCsvData(null);
    }
  }, [csvResponse, csvLoading, csvError]);

  const clearFilters = () => {
    setRequest(initialRequest);
    setLimit(10);
    setOffset(0);
    getAll({ ...initialRequest, limit: 10, offset: 0 });
  };

  const handlePageChange = (pageNumber) => {
    const newOffset = (pageNumber - 1) * limit;
    setOffset(newOffset);
    setRequest((prevState) => {
      const updated = { ...prevState, offset: newOffset };
      getAll(updated);
      return updated;
    });
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setLimit(newPerPage);
    setOffset(0);
    setRequest((prevState) => {
      const updated = { ...prevState, limit: newPerPage, offset: 0 };
      getAll(updated);
      return updated;
    });
  };

  const columns = [
    {
      name: "SNo",
      cell: (row, idx) => offset + Number(idx + 1),
      sortable: true,
      width: "80px",
    },
    {
      name: "User Details",
      cell: (row) => (
        <div className="flex flex-col py-1">
          <span className="font-semibold text-gray-900">{row.name || "N/A"}</span>
          <span className="text-xs text-gray-500">{row.mobile ? `Mob: ${row.mobile}` : ""}</span>
          <span className="text-xs text-gray-500">{row.email ? `Email: ${row.email}` : ""}</span>
          <span className="text-xs text-gray-400">{row.user_id ? `ID: ${row.user_id}` : ""}</span>
        </div>
      ),
      sortable: true,
      minWidth: "180px",
    },
    {
      name: "Notification",
      cell: (row) => (
        <div className="flex flex-col py-1 max-w-xs md:max-w-md">
          {row.notification?.notification_type && (
            <span className="text-xs font-medium uppercase tracking-wider text-blue-600 mb-0.5">
              {row.notification.notification_type}
            </span>
          )}
          <span className="font-medium text-gray-800 text-sm">
            {row.notification?.title || "No Title"}
          </span>
          <span className="text-xs text-gray-500 mt-1 break-words whitespace-normal line-clamp-3">
            {row.notification?.body || "No Message Body"}
          </span>
        </div>
      ),
      sortable: true,
      minWidth: "250px",
    },
    {
      name: "Status",
      cell: (row) => {
        const statusVal = (row.status || "").toUpperCase();
        let badgeColor = "bg-yellow-100 text-yellow-800 border-yellow-200";
        if (statusVal === "SENT" || statusVal === "SUCCESS") {
          badgeColor = "bg-green-100 text-green-800 border-green-200";
        } else if (statusVal === "FAILED") {
          badgeColor = "bg-red-100 text-red-800 border-red-200";
        }
        return (
          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${badgeColor}`}>
            {statusVal || "PENDING"}
          </span>
        );
      },
      sortable: true,
      width: "120px",
    },
    {
      name: "Sent At",
      cell: (row) => (row.sent_at ? new Date(row.sent_at).toLocaleString() : "-"),
      sortable: true,
      width: "180px",
    },
    {
      name: "Details / Error",
      cell: (row) => (
        <div className="max-w-xs text-xs text-red-600 break-words whitespace-normal py-1">
          {row.last_error || "-"}
        </div>
      ),
      sortable: true,
      minWidth: "150px",
    },
  ];

  return (
    <div className="flex w-full bg-gray-200 h-full justify-center">
      <div className="w-full">
        {/* Top Header */}
        <div className="border-b bg-white border-gray-300 pl-6 py-3 shadow-sm text-xl font-bold flex flex-col md:flex-row md:items-center justify-between pr-6">
          <div>
            <span>Notification History</span>
            <p className="text-xs font-normal text-gray-500 mt-0.5">
              View and filter jobs processed by the Notification Engine
            </p>
          </div>
          <LinksComponent />
        </div>

        {/* Action Header: Export CSV */}
        <div className="pl-3 pt-3">
          <CustomExport
            fetchData={getAllForCsv}
            data={csvData}
            defaultColumnExport={[
              "name",
              "mobile",
              "email",
              "user_id",
              "notification_type",
              "notification_title",
              "notification_body",
              "status",
              "sent_at",
              "last_error",
            ]}
            filename="notification_history"
          />
        </div>

        {/* Content Body */}
        <div className="p-3">
          <div className="w-full bg-white shadow rounded-lg">
            {/* Filters Section */}
            <div className="border-b border-gray-300 bg-gray-50 rounded-t-lg p-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 items-end">
                {/* Mobile Filter */}
                <div className="relative">
                  <label className="block text-xs font-medium capitalize leading-6 text-gray-900 mb-1">
                    Mobile Number
                  </label>
                  <input
                    type="search"
                    className="w-full px-3 py-1.5 text-sm placeholder:text-gray-400 text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                    placeholder="Enter mobile..."
                    value={request.mobile}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRequest((prev) => ({ ...prev, mobile: value }));
                    }}
                  />
                </div>

                {/* User ID Filter */}
                <div className="relative">
                  <label className="block text-xs font-medium capitalize leading-6 text-gray-900 mb-1">
                    User DB ID
                  </label>
                  <input
                    type="search"
                    className="w-full px-3 py-1.5 text-sm placeholder:text-gray-400 text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                    placeholder="Enter user id..."
                    value={request.user_id}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRequest((prev) => ({ ...prev, user_id: value }));
                    }}
                  />
                </div>

                {/* Start Date */}
                <div className="relative">
                  <label className="block text-xs font-medium capitalize leading-6 text-gray-900 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-1.5 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                    value={request.start_date}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRequest((prev) => ({ ...prev, start_date: value }));
                    }}
                  />
                </div>

                {/* End Date */}
                <div className="relative">
                  <label className="block text-xs font-medium capitalize leading-6 text-gray-900 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-1.5 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                    value={request.end_date}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRequest((prev) => ({ ...prev, end_date: value }));
                    }}
                  />
                </div>

                {/* Status Dropdown */}
                <div className="relative">
                  <label className="block text-xs font-medium capitalize leading-6 text-gray-900 mb-1">
                    Status
                  </label>
                  <select
                    className="w-full px-3 py-1.5 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp h-auto"
                    value={request.status}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRequest((prev) => ({ ...prev, status: value }));
                    }}
                  >
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="sent">Sent</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
              </div>

              {/* Actions row */}
              <div className="flex justify-end gap-2 mt-3">
                <button
                  className="btn btn-outline-primary pdbtn_all px-4 py-1.5 text-sm font-medium rounded-lg"
                  onClick={() => getAll()}
                  disabled={jobsLoading}
                >
                  {jobsLoading ? "Searching..." : "Search"}
                </button>
                <button
                  className="btn btn-outline-primary pdbtn_all px-4 py-1.5 text-sm font-medium rounded-lg"
                  onClick={clearFilters}
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Data Table */}
            <DataTableComponent
              keyField={(row) => row.id}
              columns={columns}
              data={myList}
              loading={jobsLoading}
              totalRows={totalRows}
              handlePerRowsChange={handlePerRowsChange}
              handlePageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationHistory;
